import Button from './Button';
import { resolveInterstitialURI } from '../../../../lib/utils';
import { DATA_TRACK_TYPE_CTA } from '../../../../lib/constants';

/**
 * ShopNowButton Component
 *
 * A specialized button component that generates a tracked shop redirect URL with analytics data.
 * It utilizes the `Button` component, so accepts all params from Button and ButtonUI interface.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.shopSlug - The unique identifier (slug) of the shop.
 * @param {string} props.offerSlug - The unique identifier (slug) of the offer.
 * @param {string} props.adSlug - The unique identifier (slug) of the ad.
 * @param {string} props.locale - The locale used for redirection.
 * @param {function} props.trackAnalytics - track analytics function to accept redirectUrl
 * returns object with params to destructure into Button component
 * @param {Object} [props.rest] - Additional props that will be spread into the `Button` component.
 *
 * @example
 * // Example usage with analytics tracking
 * <ShopNowButton
 *   shopSlug="amazon"
 *   offerSlug="special-deal"
 *   adSlug="banner-ad-123"
 *   locale="en"
 *   trackAnalytics={(redirectUrl) => trackAnalytics(
       DOM_AD_SMALL,
       `${slug}-primary`,
       redirectUrl,
       tags
     )}
 *   // other props that are spread into Button component
 * />
 *
 * @returns {JSX.Element} The rendered `ShopNowButton` component.
 */

const ShopNowButton = ({
  shopSlug,
  offerSlug,
  adSlug,
  locale,
  trackAnalytics,
  ...rest
}) => {
  const {
    externalRedirectUrl: interstitialURI,
  } = resolveInterstitialURI({
    shopSlug,
    offerSlug,
    adSlug,
    locale,
  });

  const analytics = trackAnalytics(interstitialURI);

  return (
    <Button
      dataTrackType={DATA_TRACK_TYPE_CTA}
      target="_blank"
      rel="opener"
      {...analytics}
      {...rest}
    />
  );
};

export default ShopNowButton;
